import React, { useState, useRef, useEffect } from "react";
import { io } from "socket.io-client";
import { RoomCard } from "../../components/generic-components/card/CardComponent";
import { CapatityObjInterface, RoomBookingInterface } from "../../components/room-booking/RoomBookingInterface";
import { useTranslation } from "@me8eon/translation";
import { useTranslation as useTranslate } from "@me8eon/translation";
import { VoiceAssist } from "./VoiceAssist";
import { useFirstName } from "../../components/ai-assist-refactored/hooks/useFirstName";
import { useLanguage } from "@me8eon/language";
import greet from "./animate/greet.raw.svg";
import speak from "./animate/speak.raw.svg";
import listening from "./animate/listening.raw.svg";
import initial from "./animate/initial.raw.svg";

import "./Intract.scss"
import { EonUiButton, EonUiCheckbox } from "@eon-ui/eon-ui-components-react";
import { Icon } from "@me8eon/icons";
import { isLocalHost } from "../../util/common-util";
import { Welcome } from "./welcome";
import { doElasticSearchForVoiceAssist, findIntentFromQuery } from "./VoiceAssistUtils";
import useRoomBookingStore from "../../store/roomBookingStore";
import { formatDate, getFormattedDateTime } from "../../util/date";
import { AzureVoiceAssist } from "./AzureVoiceAssist";

const imageMap: any = {
    greet,
    speak,
    listening
};

type MessageType = "user" | "system" | "assistant";

export interface Message {
    jql?: any;
    content: string;
    role: "user" | "system" | "assistant";
    action?: string;
}

type DisplayEvent = "booking-success" | "booking-failure" | "view-bookings" | "cancel-bookings" | null;

export const Communicate = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState<string>("");
    const chatRef = useRef<HTMLDivElement | null>(null);
    const socketRef = useRef<ReturnType<typeof io> | null>(null);
    const t = useTranslation("room-booking");
    const translate = useTranslate();
    const [availableRooms, setAvailableRooms] = useState<RoomBookingInterface[]>([]);
    const [seatings, setSeatings] = useState<CapatityObjInterface[]>([]);
    const [roomsWithSeating, setRoomsWithSeating] = useState<RoomBookingInterface[]>([]);
    const [userToken, setUserToken] = useState("");
    const firstName = useFirstName()
    const [lang] = useLanguage();
    const [socketRunning, setSocketRunning] = useState(false);
    const [buttonEnabledVoice, setButtonEnabledVoice] = useState(false);
    const [checkboxEnabledVoice, setCheckboxEnabledVoice] = useState(true);
    const [displayEvent, setDisplayEvent] = useState<DisplayEvent>(null);
    const transcriptDivRef = useRef<HTMLDivElement | null>(null)
    const [isSendToSocket, setIsSendToSocket] = useState<boolean>(false);
    const [gingcoMeetingId, setGingcoMeetingId] = useState<string>("");
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [userBookings, setUserBookings] = useState<any[]>([]);
    const { userLocalTimezone } = useRoomBookingStore();

    useEffect(() => {
        const token = localStorage.getItem("accessToken");
        if (token) {
            setUserToken(token);
        }
    }, []);

    useEffect(() => {
        if (!availableRooms || !seatings) return;

        const updatedRooms = availableRooms.map(room => {
            // Find seating objects that match this room by seating_id
            const matchingSeatings = room.seating_ids.map((seatId: number | CapatityObjInterface) => {
                // if seating is already object, return it
                if (typeof seatId === "object") return seatId;
                // else find seating object in global seatings list
                return seatings.find((s: CapatityObjInterface) => s.seating_id === seatId);
            }).filter(Boolean); // remove undefined if not found

            return {
                ...room,
                seating_ids: matchingSeatings
            };
        });

        setRoomsWithSeating(updatedRooms);
    }, [availableRooms, seatings]);

    const startBot = () => {
        setSocketRunning(true);
        setButtonEnabledVoice(true);
        if (socketRef.current) return; // prevent multiple connections
        const url = isLocalHost() ? "http://localhost:3000" : "";
        const socket = io(url, {
            path: `/service/test`,
            query: { name: firstName, lang }
        });
        socketRef.current = socket;

        socket.on("connect", () => {
            const tz =
                window.localStorage.getItem("timezone_id") ||
                Intl.DateTimeFormat().resolvedOptions().timeZone ||
                "UTC";
            socket.emit("client:timezone", { timezone_id: tz });
            console.log("Connected!")
        });
        socket.on("assistant", (msg: string | { rooms: RoomBookingInterface[], message: string, seatings: CapatityObjInterface[] }) => {
            if (typeof msg === "string") {
                // old behavior
                appendMessage("Assistant", msg, "assistant");
            } else if (msg && typeof msg === "object") {
                setDisplayEvent(null);
                // handle structured response
                appendMessage("Assistant", msg.message, "assistant");

                if (msg.rooms && Array.isArray(msg.rooms)) {
                    setAvailableRooms(msg.rooms)
                }
                if (msg.seatings && Array.isArray(msg.seatings)) {
                    setSeatings(msg.seatings)
                }
            }
        });
        socket.on("event:success:booking", (meetingId: string)=> {setRoomsWithSeating([]);setDisplayEvent("booking-success"); setIsSendToSocket(false);setGingcoMeetingId(meetingId)})
        socket.on("event:failure:booking", ()=> {setRoomsWithSeating([]);setDisplayEvent("booking-failure"); setIsSendToSocket(false);})
        socket.on("event:error:gingcoToken", ()=> {setIsSendToSocket(false);})
        socket.on("event:error:exceptionCounterExceeded", ()=> {setIsSendToSocket(false);})
        socket.on("event:success:viewbooking", (bookings)=> {setIsSendToSocket(false);setRoomsWithSeating([]);setDisplayEvent("view-bookings");setUserBookings(bookings || []);});
        socket.on("event:failure:viewbooking", () => {setIsSendToSocket(false);setRoomsWithSeating([]);});
        socket.on("event:success:cancelbooking", ()=> {setIsSendToSocket(false);setRoomsWithSeating([]);setDisplayEvent("cancel-bookings");setUserBookings([]);});
        socket.on("event:failure:cancelbooking", ()=> {setIsSendToSocket(false);setRoomsWithSeating([]);});
        socket.on("disconnect", () => {
            console.log("Disconnected");
            setSuggestions([]);
            setGingcoMeetingId("")
            setDisplayEvent(null);
            setRoomsWithSeating([]);
            setIsSendToSocket(false)
        });
    };

    const stopBot = () => {
        setMessages([]);
        setSuggestions([]);
        setRoomsWithSeating([])
        setButtonEnabledVoice(false);
        setSocketRunning(false);
        setDisplayEvent(null);
        if (!socketRef.current) return;
        socketRef.current.disconnect();
        socketRef.current = null;
        console.log("Socket disconnected");
    }

    const appendMessage = (sender: string, content: string, role: MessageType) => {
        setMessages(prev => [...prev, { sender, content, role }]);
        setTimeout(() => {
            if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }, 50);
    };

    const sendMessage = async (hearedText?: string) => {
        const text = hearedText ? hearedText.trim() : input.trim();
        if (!text) return;
        setSuggestions([]);
        appendMessage("You", text, "user");

        if(isSendToSocket){
            socketRef.current?.emit("user", {
                text,
                history: [...messages, { content: text, role: "user" }], // include latest
                accesstoken: userToken,
            });
            setInput("");
            return;
        }

        const intentResponse = await findIntentFromQuery(text);
        if(intentResponse?.intent === "booking" || intentResponse?.intent === "hot-desk" || intentResponse?.intent === "it-support"){ 
            setIsSendToSocket(true);
            const token = localStorage.getItem("accessToken") || "";
            socketRef.current?.emit("user", {
                text,
                history: [...messages, { content: text, role: "user" }], // include latest
                accesstoken: token,
            });
        } else if(intentResponse?.intent === "other" || intentResponse?.language === "not_supported"){
            setGingcoMeetingId("")
            setDisplayEvent(null);
            setRoomsWithSeating([]);
            doElasticSearchForVoiceAssist(text).then((resp) => {
                let parsedResp = "";
                if(typeof resp === "string"){
                    parsedResp = resp;
                } else if(typeof resp === "object" && resp.answer){
                    parsedResp = resp.answer;
                    setSuggestions(resp.suggestions || []);
                }
                appendMessage("Assistant", parsedResp, "assistant");
            }).catch((err) => {
                console.error("Error in ElasticSearch for Voice Assist:", err);
                appendMessage("Assistant", "I'm sorry, I couldn't process your request at the moment.", "assistant");
            })
        }
        setInput("");
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") sendMessage();
    };

    const handleCardClick = (clickedCard: RoomBookingInterface) => {
        console.log({ clickedCard });
        appendMessage("You", `${clickedCard.object_id}`, "user");
        console.log({ userToken1: userToken });
        setAvailableRooms([]);
        setRoomsWithSeating([]);
        setSeatings([]);
        socketRef.current?.emit("user", {
            text: `${clickedCard.object_id}`,
            history: messages,
            accesstoken: userToken
        });
    }

    const getLoaderName = (): string => {
        let name = messages.length > 1 ? "listening" : "greet";
        return imageMap[name] ?? "";
    };

    const fetchSocketData = (): void => {
        socketRef.current?.emit('client:getdata', (err: Error, data: any) => {
            if (err) return console.error(err);
            console.log(data);
        });
    }

    const fetchAzureSpeechToken = async (): Promise<{
        token: string;
        region: string;
        expiresInMinutes: number;
    }> => {
        return new Promise((resolve, reject) => {
            socketRef.current?.emit("speech-token", (response: any) => {
                if (response?.error) {
                    return;
                    return reject(new Error(response.error));
                }
                const { token, region, expiresInMinutes } = response;
                resolve({ token, region, expiresInMinutes });
            });
        });
    };

//   const computeHeightFromX = (x: number) => Math.max(100, Math.round(window.innerHeight - x - 90));

//   useEffect(() => {
//     const el = transcriptDivRef.current;
//     if (!el) return;
//     let prevX: number | null = null;

//     const updateFromRect = () => {
//       const rect = el?.getBoundingClientRect();
//       if (!rect || !el) return;
//       const x = Math.round(rect.top);
//       console.log(x, prevX, rect, window.innerHeight)
//       if (x !== prevX) {
//         prevX = x;
//         el.style.height = `${computeHeightFromX(x)}px`;
//       }
//     };

//     const resizeObserver = new ResizeObserver(updateFromRect);
//     resizeObserver.observe(el);

//     const mutationObserver = new MutationObserver(updateFromRect);
//     mutationObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });

//     const onResize = () => updateFromRect();
//     const onScroll = () => updateFromRect();

//     window.addEventListener('resize', onResize);
//     window.addEventListener('scroll', onScroll, { passive: true });

//     updateFromRect();

//     return () => {
//       resizeObserver.disconnect();
//       mutationObserver.disconnect();
//       window.removeEventListener('resize', onResize);
//       window.removeEventListener('scroll', onScroll);
//     };
//   }, []);


    return (
        <div>
            <div className="voice-assist voice-assist-header">
                <div className="col1" style={{ display: "flex", gap: "8px" }}>
                    <EonUiButton
                        rank="secondary"
                        size="small"
                        text={translate("chatBot.newChat")}
                        style={{ flex: 1 }}
                        fullWidth
                        disabled={socketRunning}
                        onClick={startBot}>
                    </EonUiButton>
                    <EonUiButton
                        rank="secondary"
                        size="small"
                        style={{ flex: 1 }}
                        text={translate("chatBot.close")}
                        disabled={!socketRunning}
                        fullWidth
                        onClick={stopBot}>
                    </EonUiButton>
                </div>
                <div className="col2">
                    <EonUiCheckbox
                        checked={checkboxEnabledVoice}
                        onValueChanged={() => setCheckboxEnabledVoice(!checkboxEnabledVoice)}
                        name="voice_enable"
                        label={lang === "de" ? "Stimme aktivieren" : "Enable Voice"}
                    />
                </div>
            </div>
            <div className="voice-assist voice-assist-main">
                <div className="col1">
                    {!socketRunning && (
                        <img className="img1" src={initial} alt="loader" />
                    )}
                    {displayEvent === "booking-success" && (
                        <div className="display-event-div display-event--booking--success">
                            <div className="display-event--booking--success-box">
                                <Icon name="success" /> Room booking is successful
                            </div>
                            <p className="display-event--booking--success-info">
                                {lang === "de" ? `Ihre Meeting-ID ist ` : `Your meeting ID is `}<b>{gingcoMeetingId}</b>
                            </p>
                        </div>
                    )}
                    {displayEvent === "booking-failure" && (
                        <div className="display-event-div display-event--booking--failure">
                            Something went wrong. Please try again later.
                        </div>
                    )}
                    {
                        displayEvent === "view-bookings" && (
                            <div className="display-event-div display-event--view--bookings">
                                {userBookings.length === 0 ? (
                                    <p className="display-event--view--bookings-info">
                                        {lang === "de"
                                            ? "Sie haben derzeit keine Buchungen."
                                            : "You have no bookings currently."}
                                    </p>
                                ) : (
                                    <>
                                        <p className="display-event--view--bookings-info">
                                            {lang === "de"
                                                ? "Ihre Buchungen:"
                                                : "Your Bookings:"}
                                        </p>
                                        <div className="display-event--view--bookings-box">
                                            {userBookings.map(
                                                (booking, index) => (
                                                    <div key={index}>
                                                        <p>
                                                            Meeting ID:{" "}
                                                            {booking.booking_id}{" "}
                                                            |{" "}
                                                            <strong>
                                                                {booking.topic}
                                                            </strong>
                                                        </p>
                                                        <p>
                                                            📅{" "}
                                                            {formatDate(booking.begin.timestamp)}{" "}
                                                            | ⏰
                                                            {getFormattedDateTime(
                                                                booking.begin
                                                                    .timestamp *
                                                                    1000,
                                                                "H:i",
                                                                true,
                                                                userLocalTimezone
                                                            )}{" "}
                                                            - {" "}
                                                            {getFormattedDateTime(
                                                                booking.end
                                                                    .timestamp *
                                                                    1000,
                                                                "H:i",
                                                                true,
                                                                userLocalTimezone
                                                            )} | 📍{" "}
                                                            {
                                                                booking.object_name
                                                            }{" "}
                                                            | 👥{" "}
                                                            {booking.capacity}
                                                        </p>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    }
                    {displayEvent === "cancel-bookings" && (
                        <div className="display-event-div display-event--booking--success">
                            <div className="display-event--booking--success-box">
                                <Icon name="success" /> Room booking is
                                cancelled
                            </div>
                        </div>
                    )}
                    {messages.length > 0 && availableRooms.length === 0 && displayEvent === null && (
                        <img src={`${getLoaderName()}?t=${messages.length}`} alt="loader" />
                    )}
                    {displayEvent === null && roomsWithSeating.length > 0 && (
                        <div className="room-card-wrapper">
                            <div className="card-wrapper">
                                {roomsWithSeating.map((cardData, index) => {
                                    return (
                                        <RoomCard
                                            key={`${index.toString()}`}
                                            cardData={cardData}
                                            onCardClick={handleCardClick}
                                            translator={t}
                                            withId
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                <div className="col2" ref={transcriptDivRef}>
                    {messages.length === 0 && (
                        <div className="img-wrapper">
                            <Welcome text={lang === "de" ? "Willkommen" : "Welcome"} />
                        </div>
                    )}
                    {messages.length > 0 && (<div ref={chatRef} style={{ maxHeight: "calc(100dvh - 262px)", overflowY: "auto", position: "sticky", top: "170px", background: "#fff", padding: 15, borderRadius: 8, boxShadow: "0 2px 6px rgba(0,0,0,0.15)", marginBottom: 10, zIndex: 999 }}>
                        {messages.map((msg, idx) => (
                            <p key={idx} style={{ margin: "6px 0", paddingBottom: "12px", borderBottom: "1px solid #ebebeb", textAlign: msg.role === "user" ? "right" : "left" }}>
                                <b style={{ color: msg.role === "assistant" ? "#ea1b0a" : "#212121" }}>{msg.role.charAt(0).toUpperCase() + msg.role.slice(1)}:</b> {msg.content}
                            </p>
                        ))}
                    </div>)}
                </div>
            </div>

            <div className="voice-assist-footer">
                <div className="main-content ">
                    <div className="me8eon-responsive-container">
                        <div className="voice-assist">
                            <div className="col1">
                                {suggestions.length > 0 && (
                                    <div className="suggestions-list">
                                        {suggestions.map((suggestion, index) => (
                                            <div key={index} onClick={() => sendMessage(suggestion)}>
                                                {suggestion}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div style={{ display: "flex" }}>
                                    <input
                                        type="text"
                                        placeholder={lang === "de" ? "Hier eingeben" : "Type here"}
                                        value={input}
                                        disabled={!socketRunning}
                                        onChange={e => setInput(e.target.value)}
                                        onKeyUp={handleKeyUp}
                                        style={{ flex: 1, padding: 10, borderRadius: 4, border: "1px solid #ccc", outline: "none", width: "100%" }}
                                    />
                                    <button disabled={!input} onClick={() => sendMessage()} style={{ padding: "10px 15px", border: "1px solid #ccc", marginLeft: 5, borderRadius: 4, color: "#fff", cursor: "pointer" }}>
                                        <Icon name="send" style={{ transform: "rotate(45deg)" }} />
                                    </button>
                                </div>
                            </div>
                            <div className="col2">&nbsp;</div>
                        </div>
                    </div>
                </div>
            </div>
            {/* <VoiceAssist
                sendMessage={sendMessage}
                messages={messages}
                buttonEnabledVoice={buttonEnabledVoice}
                checkboxEnabledVoice={checkboxEnabledVoice}
            /> */}
            <AzureVoiceAssist
                sendMessage={sendMessage}
                messages={messages}
                buttonEnabledVoice={buttonEnabledVoice}
                checkboxEnabledVoice={checkboxEnabledVoice}
                recognitionLanguage="en-US"
                voiceName="en-US-JennyNeural"
                speakingRate={1.0}
                speechTokenEndpoint={`${
                    isLocalHost() ? "http://localhost:3000" : ""
                }/service/speech-token`} // your backend route
                fetchAzureSpeechToken={fetchAzureSpeechToken}
            />
        </div>
    );
};
